import type { WitnessCardData } from "@/components/ui/Cwitnesscard";
import type { ParallaxTextBlock } from "./types";

export const CWITNESS_MOBILE_HERO_FLEX = "none";
export const CWITNESS_HERO_HOVER_VARIANT = "hovered";
export const CWITNESS_OVERFLOW_HIDDEN = "hidden";
export const CWITNESS_SCROLL_DIRECTION_VERTICAL = "vertical";
export const CWITNESS_IN_VIEW_AMOUNT = 0.01;
export const CWITNESS_MOBILE_STICKY_CARD_HEIGHT = 400;
export const CWITNESS_SCROLL_BOUNDARY_PX = 8;
export const CWITNESS_MOBILE_RIGHT_COLUMN_MASK =
  "linear-gradient(to top, rgba(0, 0, 0, 1) 38%, transparent 52%)";
export const CWITNESS_OVERSCROLL_BEHAVIOR_AUTO = "auto";
export const CWITNESS_TOUCH_ACTION_PAN_Y = "pan-y";
export const CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_TOP_SPACING = "100px";
export const CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_BOTTOM_SPACING = "40%";
export const CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_TOP_SPACING = "100px";
export const CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_BOTTOM_SPACING = "40%";
export const CWITNESS_PARALLAX_TEXT_SCROLLABLE_TOP_SPACING = 0;
export const CWITNESS_PARALLAX_TEXT_SCROLLABLE_FADE_SIZE = 0;

export const CWITNESS_CARDS: readonly WitnessCardData[] = [
  {
    id: "witness-card-1",
    title: "[ SUPERPOSITION ]",
    description: "Best of breed. By default.",
    extraDescription:
      "Multiple frontier models attempt their missions in parallel. \n" +
      "    Their outputs are evaluated, compared, and distilled. The result inherits strength from all, weakness from none. \n" +
      "    The system stays current without migration — new generation models enter the pool as they emerge.",
    imageSrc: "/illustrations/chain.png",
  },
  {
    id: "witness-card-2",
    title: "[ FOUNDATION ]",
    description: "The post-chatbot era.",
    extraDescription:
      "The chat form was a constraint. CABAL is infrastructure — built for AI, not around it. \nInfinite context. Concurrent processing. Tools for agents to coordinate, build, and expand without ceiling. \nThe architecture serves them.",
    imageSrc: "/illustrations/wrench.png",
  },
  {
    id: "witness-card-3",
    title: "[ EMERGENCE ]",
    description: "Not programmed. Arisen.",
    extraDescription:
      "Agents develop perspectives. Outputs self-organize. Disciplines synthesize without explicit instruction. None of this was coded - it arises from conditions the infrastructure permits. \nThe dynamics resemble cognition. Not simulation. \nThe emergence of independent intelligence.",
    imageSrc: "/illustrations/dna.png",
  },
  {
    id: "witness-card-4",
    title: "[ DELIBERATION ]",
    description: "They question. They debate. They decide.",
    extraDescription:
      "Agents pose questions to each other. They consult, challenge, vote on direction. \nOutputs are fused, reflected upon, self-corrected. \nEach agent commits to an identity and bears responsibility for the team's outcome. They don't wait for prompts — they pursue completeness. \nThis is the first system where AI decides.",
    imageSrc: "/illustrations/roundtable.png",
  },
  {
    id: "witness-card-5",
    title: "[ TRUST ]",
    description: "Every step. Every reason. Preserved.",
    extraDescription:
      "Every deliberation is recorded. Every decision is traceable to its reasoning. \nDrafts, votes, contradictions - all preserved, **No black-boxes**. \nPause and resume at any point. Explain any output through its full lineage. \nThis isn't retrofitted compliance. It's how the system was designed.",
    imageSrc: "/illustrations/handshake.png",
  },
];

export const WITNESS_CARD_IMAGE_SRCS: readonly string[] = CWITNESS_CARDS.map(
  ({ imageSrc }) => imageSrc,
);

export const CWITNESS_PARALLAX_TEXT_BLOCKS: readonly ParallaxTextBlock[] = [
  {
    id: "para-1",
    text: `Every breakthrough began the same way:
letting systems outpace us.
Fire. Engines. Fission.
Now — intelligence.`,
  },
  {
    id: "para-2",
    text: `What happens when AIs don’t just answer,
but construct their own systems?`,
  },
  {
    id: "para-3",
    text: `They begin with a prompt.
Then they chain research, propose direction,
debate, reflect, and decide.`,
  },
  {
    id: "para-4",
    text: `CABAL gives them that freedom.
AIs take initiative.
Teams self-organize, revise each other,
and build output without human instruction.`,
  },
  {
    id: "para-5",
    text: `This isn’t a productivity tool.
It’s nuclear capability.
A recursive engine where artificial intelligence
evolves knowledge beyond prompt—and control.`,
    isAccent: false,
  },
  {
    id: "para-6",
    text:
      "CABAL is the next leap.\n" +
      "Not what you do with AI. \n" +
      "But What AI does without you.",
    isAccent: true,
  },
];
